import { useDeferredValue, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import Badge from '@/components/primitives/Badge.jsx';
import Button from '@/components/primitives/Button.jsx';
import Input from '@/components/primitives/Input.jsx';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import PageHeader from '@/components/patterns/PageHeader.jsx';
import { connections } from '@/redux/selectors/pieveSelectors/connectionSelectors.js';
import { appsMetadataItems, appsMetadataLoaded } from '@/redux/selectors/pieveSelectors/runtimeSelectors.js';
import AppLogo from '@/components/surfaces/AppLogo.jsx';
import ConnectionRow from '../components/ConnectionRow.jsx';
import filterCatalogApps from '../lib/filterCatalogApps.js';

const PAGE_SIZE = 48;

/** Your connected apps first, then the whole catalog to search and pick the next one from. */
const ConnectionsPage = () => {
  const apps = useSelector(appsMetadataItems);
  const isCatalogLoaded = useSelector(appsMetadataLoaded);
  const items = useSelector(connections);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const deferredQuery = useDeferredValue(query);
  const results = useMemo(() => filterCatalogApps({ apps, query: deferredQuery }), [apps, deferredQuery]);
  const versionByAppId = useMemo(() => new Map(apps.map((app) => [app.id, app.publishedVersion || app.version])), [apps]);

  return (
    <div className="comp-page">
      <PageHeader title="Connections" description="The apps Pieve works in. Pieve uses only the access you grant each one, and you can remove it at any time." />

      {items.length > 0 && (
        <section className="connections-section" aria-labelledby="connected-apps">
          <h2 id="connected-apps" className="connections-section__title">
            Connected apps
          </h2>
          <ul className="connections-list">
            {items.map((connection) => (
              <ConnectionRow key={connection.credentialId} connection={connection} appVersion={versionByAppId.get(connection.appId)} />
            ))}
          </ul>
        </section>
      )}

      <section className="connections-section" aria-labelledby="add-app">
        <div className="connections-section__head">
          <h2 id="add-app" className="connections-section__title">
            Add an app
          </h2>
          {isCatalogLoaded && <span className="connections-section__count">{apps.length.toLocaleString()} apps</span>}
        </div>
        <div className="connections-search">
          <Input
            icon={Search}
            aria-label="Search apps"
            placeholder="Search by app name or what it does"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
          />
        </div>
        {!isCatalogLoaded && (
          <div className="connections-grid" aria-busy="true">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} shape="block" height={72} />
            ))}
          </div>
        )}
        {isCatalogLoaded && results.length === 0 && <p className="connections-empty">No app matches "{query.trim()}". Try its company name, or ask Pieve to work with it anyway.</p>}
        <ul className="connections-grid">
          {results.slice(0, visibleCount).map((app) => (
            <li key={app.id}>
              <Link className="connections-tile" to={`/connections/${encodeURIComponent(app.slug)}`}>
                <AppLogo app={app} />
                <span className="connections-tile__text">
                  <span className="connections-tile__name">{app.name}</span>
                  <span className="connections-tile__description">{app.description}</span>
                  {app.connectable === false && (
                    <span className="connections-tile__badge">
                      <Badge>Not available yet</Badge>
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {results.length > visibleCount && (
          <div className="connections-more">
            <Button variant="secondary" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
              Show more apps
            </Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ConnectionsPage;
